import React from 'react';
import { Redirect } from 'wouter';

import {
  ACCOUNT_HOME_PAGE,
  ACCOUNT_OWNER_ONLY,
  ACCOUNT_PATHS,
  accountPageInSprint,
  type AccountPageId,
} from '@/lib/accountPaths';
import { useAccountRecord } from './AccountRecordContext';
import { showsTeamPage } from './accountModel';

export { AccountPersonal } from './AccountPersonal';
export { AccountPassword } from './AccountPassword';
export { AccountGem } from './AccountGem';
export { AccountBusiness } from './AccountBusiness';
export { AccountPublic } from './AccountPublic';
export { AccountTeam } from './AccountTeam';
export { AccountMembership, AccountBilling } from './AccountBilling';
export { AccountDocuments } from './AccountDocuments';

/**
 * AR-380 — the visibility rules, applied to a deep link.
 *
 * Your Business and Billing are the Owner's; Team + Access belongs to whoever
 * manages a team. A member who cannot see a page lands on User Account rather
 * than on an empty page or an error — they see nothing, not a zero. There is no
 * overview page to land on any more, so the module home is the destination.
 */
export const AccountPageGuard: React.FC<{
  page: AccountPageId;
  children: React.ReactNode;
}> = ({ page, children }) => {
  const { role, viewer } = useAccountRecord();
  // AR-381 resolves this FROM THE DATA: `viewer` carries the member's team
  // role out of the migrated model, so the rule no longer rests on a record
  // that is editable from the console. Until the model answers, the auth
  // context's role stands, which is what it always did.
  const isOwner = viewer ? viewer.isOwner : role === 'Owner';

  // v15 moved the owner-only set: it was Membership and Billing, it is now
  // Your Business and Billing (D-ACCT-39). Plan + Agreements is everyone's,
  // reduced for an associate rather than withheld (D-ACCT-40).
  if (ACCOUNT_OWNER_ONLY.includes(page) && !isOwner) {
    return <Redirect to={ACCOUNT_PATHS[ACCOUNT_HOME_PAGE]} />;
  }
  if (page === 'team' && !showsTeamPage(role)) {
    return <Redirect to={ACCOUNT_PATHS[ACCOUNT_HOME_PAGE]} />;
  }
  // A page this sprint does not carry is not reachable by address either.
  if (!accountPageInSprint(page)) {
    return <Redirect to={ACCOUNT_PATHS[ACCOUNT_HOME_PAGE]} />;
  }
  return <>{children}</>;
};
